import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { StepNavigationService } from '../../../../shared/services/step-navigation/step-navigation.service';
import { Router } from '@angular/router';
import { StepLink } from '../../../../shared/models/step-link';
import { Subject, filter, takeUntil, tap } from 'rxjs';
import { PageHeaderNavComponent } from '../../../../../../shared/components/page-header-nav/page-header-nav.component';
import { NavigationText } from '../../../../../../core/constants/navigation-text';
import { NavigationOptions } from '../../../../../../shared/components/page-header-nav/models/navigation-options';
import { PageHeaderNavItem } from '../../../../../../shared/components/page-header-nav/models/page-header-nav-item';
import { StepNavigationButtonVisibilityService } from '../../../../shared/services/step-navigation-button-visibility/step-navigation-button-visibility.service';
import { StepNavigationButtonService } from '../../../../shared/services/step-navigation-button/step-navigation-button.service';
import { RouteConstants } from '../../../../../../core/constants/routes';
import { ApplicationState } from '../../../../../../core/models/application-state/application-state';
import { ApplicationStateService } from '../../../../shared/services/application-state/application-state.service';
import { CoordinationAllowedStatuses } from '../../../../shared/constants/application-filing-status';
import { hasOpenDialogs } from '../../../../../../core/functions/dialog-helper';
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent } from '../../../../../../shared/components/modal/modal.component';
import { SubmitForCoordinationDialogMessageConstants } from '../../../coordination-results/constants/submit-for-coordination-dialog-message';
import { ReturnRouteService } from '../../../../../../core/services/navigation/return-route.service';

@Component({
    selector: 'app-application-coordination-navigation-bar',
    templateUrl: './application-coordination-navigation-bar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatButtonModule, PageHeaderNavComponent],
})
export class ApplicationCoordinationNavigationBarComponent
    implements OnInit, OnDestroy
{
    navItems: NavigationOptions = {};
    applicationState: ApplicationState | null = null;

    public title: string | undefined;

    private destroy$ = new Subject<void>();

    private readonly stepNavigationButtonVisibilityService = inject(
        StepNavigationButtonVisibilityService,
    );
    private readonly stepService = inject(StepNavigationService);
    private readonly router = inject(Router);
    private readonly stepNavigationButtonService = inject(
        StepNavigationButtonService,
    );
    private readonly applicationStateService = inject(ApplicationStateService);
    private readonly dialog = inject(MatDialog);
    private readonly returnRouteService = inject(ReturnRouteService);

    ngOnInit(): void {
        this.subscribeToStepChanges();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    previous() {
        this.stepService.triggerPreviousClick();
    }

    next() {
        this.stepService.triggerNextClick();
    }

    saveAndClose() {
        this.stepService.triggerSaveAndCloseClick();
    }

    cancel() {
        const returnRoute = this.returnRouteService.consume();
        if (returnRoute) {
            this.router.navigate([returnRoute]);
        } else {
            this.router.navigate([
                RouteConstants.Dashboard.getFullRoute('INDEX'),
            ]);
        }
    }

    handoverToEwa() {
        this.stepService.triggerHandoverToEwaClick();
    }

    submitForCoordination() {
        if (
            this.applicationState?.filingStatus &&
            CoordinationAllowedStatuses.includes(
                this.applicationState.filingStatus,
            )
        ) {
            this.stepService.triggerSubmitForCoordinationClick();
        } else {
            this.displayCoordinationProhibitedModal();
        }
    }

    submitToFcc() {
        this.stepService.triggerSubmitToFccClick();
    }

    private subscribeToStepChanges(): void {
        this.stepService.currentStep$
            .pipe(
                takeUntil(this.destroy$),
                filter(
                    (currentStep): currentStep is StepLink =>
                        currentStep !== undefined,
                ),
                tap((currentStep: StepLink) => {
                    this.applicationState =
                        this.applicationStateService.getCurrentApplicationState();
                    this.title = currentStep.title;
                    this.setNavigationLinks(currentStep);
                }),
            )
            .subscribe();
    }

    private setNavigationLinks(stepLink: StepLink) {
        this.setPrimaryLinks(stepLink);
        this.setTertiaryLinks(stepLink);
        this.setSecondaryLinks(stepLink);
    }

    private setTertiaryLinks(stepLink: StepLink) {
        const tertiaryLinks = [];

        tertiaryLinks.push(
            this.stepNavigationButtonService.createTertiaryLink(
                NavigationText.CANCEL,
                this.cancel.bind(this),
            ),
        );

        if (
            this.stepNavigationButtonVisibilityService.needsSaveAndCloseButton(
                stepLink,
            )
        ) {
            tertiaryLinks.push(
                this.stepNavigationButtonService.createTertiaryLink(
                    NavigationText.SAVE_AND_CLOSE,
                    this.saveAndClose.bind(this),
                ),
            );
        }

        if (
            this.stepNavigationButtonVisibilityService.needsSubmitForCoordinationButton(
                stepLink,
            )
        ) {
            tertiaryLinks.push(
                this.stepNavigationButtonService.createTertiaryLink(
                    NavigationText.SUBMIT_FOR_COORDINATION,
                    this.submitForCoordination.bind(this),
                ),
            );
        }

        if (
            this.stepNavigationButtonVisibilityService.needsCoordinationSubmitToFccButton(
                stepLink,
            )
        ) {
            tertiaryLinks.push(
                this.stepNavigationButtonService.createTertiaryLink(
                    NavigationText.SUBMIT_TO_FCC,
                    this.submitToFcc.bind(this),
                ),
            );
        }

        this.navItems.tertiaries = tertiaryLinks;
    }

    private setSecondaryLinks(stepLink: StepLink) {
        const secondaryLinks: PageHeaderNavItem[] = [];

        if (
            this.applicationState &&
            this.applicationState.handoverToEwa != null &&
            this.stepNavigationButtonVisibilityService.needsHandoverToEwaButton(
                stepLink,
                false,
                this.applicationState.handoverToEwa,
            )
        ) {
            secondaryLinks.push(
                this.stepNavigationButtonService.createSecondaryLink(
                    NavigationText.HANDOVER_TO_EWA,
                    this.handoverToEwa.bind(this),
                ),
            );
        }

        this.navItems.secondaries = secondaryLinks;
    }

    private setPrimaryLinks(stepLink: StepLink) {
        const primaryLinks: PageHeaderNavItem[] = [];

        if (
            this.stepNavigationButtonVisibilityService.needsPreviousButton(
                stepLink,
            )
        ) {
            primaryLinks.push(
                this.stepNavigationButtonService.createPrimaryLink(
                    NavigationText.PREVIOUS,
                    this.previous.bind(this),
                ),
            );
        }

        if (
            this.stepNavigationButtonVisibilityService.needsNextButton(stepLink)
        ) {
            primaryLinks.push(
                this.stepNavigationButtonService.createPrimaryLink(
                    NavigationText.NEXT,
                    this.next.bind(this),
                ),
            );
        }

        this.navItems.primaries = primaryLinks;
    }

    private displayCoordinationProhibitedModal(): void {
        if (hasOpenDialogs(this.dialog)) {
            return;
        }

        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: 'Coordination Blocked',
                content:
                    SubmitForCoordinationDialogMessageConstants.coordinationProhibitedMessage,
                primaryAction: {
                    title: SubmitForCoordinationDialogMessageConstants.actionText,
                    action: () => {
                        this.dialog.closeAll();
                    },
                },
                isShowCancel: false,
            },
        });
    }
}
